import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Loader2 } from "lucide-react";

import { buildSeo } from "@/lib/seo";
import { MarketingLayout, Eyebrow, useHydratedTranslation } from "@/components/marketing/Layout";
import { TOPICS } from "@/lib/topics";
import { useAutoTranslate } from "@/components/AutoTranslate";

const SITE = "https://realitytwin.io";

export const Route = createFileRoute("/topics/")({
  head: () => {
    const seo = buildSeo({
      path: "/topics",
      title: "How agentic AI actually works in B2B | Reality Twin",
      description:
        "Six explainers on agentic AI, machine-to-machine business, verified identity and the legal side — each one honest about what works today and what does not.",
    });
    return {
      ...seo,
      links: [
        ...seo.links,
        { rel: "alternate", hrefLang: "en", href: `${SITE}/topics` },
        { rel: "alternate", hrefLang: "de", href: `${SITE}/de/themen` },
        { rel: "alternate", hrefLang: "x-default", href: `${SITE}/topics` },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "How agentic AI actually works in B2B",
            url: `${SITE}/topics`,
            hasPart: TOPICS.map((t) => ({
              "@type": "Article",
              headline: t.metaTitle,
              url: `${SITE}/topics/${t.slug}`,
            })),
          }),
        },
      ],
    };
  },
  component: TopicsIndex,
});

function TopicsIndex() {
  const t = useHydratedTranslation();
  const src = TOPICS.flatMap((x) => [x.name, x.heroLead]);
  const { out, busy } = useAutoTranslate(src);

  return (
    <MarketingLayout>
      <div className="py-14 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <Eyebrow>
            <span className="inline-flex items-center gap-2">
              {t("routes.topics.eyebrow", { defaultValue: "Topics" })}
              {busy && <Loader2 className="h-3 w-3 animate-spin" />}
            </span>
          </Eyebrow>
          <h1 className="mt-4 font-serif text-[36px] sm:text-[56px] leading-[1.03] tracking-[-0.03em]">
            {t("routes.topics.indexH1", {
              defaultValue: "How agentic AI actually works in B2B",
            })}
          </h1>
          <p className="mt-6 text-[17px] leading-[1.6] text-muted-foreground">
            {t("routes.topics.indexLead", {
              defaultValue:
                "Six explainers on the shift that makes this product make sense. Each one names what works today and what does not — because in a category this over-sold, the page you can check is worth more than the page that promises most.",
            })}
          </p>
        </div>

        <div className="mx-auto max-w-3xl px-6 mt-10 grid sm:grid-cols-2 gap-4">
          {TOPICS.map((topic, i) => (
            <Link
              key={topic.slug}
              to="/topics/$slug"
              params={{ slug: topic.slug }}
              className="group rounded-2xl hairline bg-card p-5 hover:bg-cream transition flex flex-col"
            >
              <div className="font-serif text-[20px] tracking-[-0.02em]">{out[i * 2]}</div>
              <p className="mt-2 flex-1 text-[14px] leading-[1.6] text-muted-foreground">
                {out[i * 2 + 1]}
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-medium">
                {t("routes.topics.read", { defaultValue: "Read" })}
                <ArrowRight
                  className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition"
                  strokeWidth={2}
                />
              </span>
            </Link>
          ))}
        </div>

        <div className="mx-auto max-w-3xl px-6 mt-10">
          <div className="rounded-2xl hairline bg-cream p-5 text-[14px] leading-[1.6] text-muted-foreground">
            {t("routes.topics.indexNote", {
              defaultValue:
                "Looking for your sector rather than the concept? The industry pages cover fourteen of them with the same discipline.",
            })}{" "}
            <Link to="/industries" className="text-foreground hover:opacity-70 underline">
              {t("routes.topics.toIndustries", { defaultValue: "Browse industries" })}
            </Link>
          </div>
        </div>
      </div>
    </MarketingLayout>
  );
}
